import React, { useState, useEffect } from 'react';
import Cookies from 'universal-cookie';
import client from '../lib/sanityClient';
import { Button } from '@/components/ui/button';
import { Pagination, DetailCard } from '@/components';
import type { PortableTextBlock as PortableTextBlockType } from '@sanity/types';

interface Slug {
  current: string;
}

interface PageParent {
  slug: Slug;
  pageParent?: PageParent;
}

interface SearchResult {
  _id: string;
  _type: string;
  title: string;
  shortTitle?: string;
  slug?: Slug;
  pageParent?: PageParent;
  language: string;
  externalUrl?: string;
  blurb?: PortableTextBlockType;
  abstract?: string;
  content?: PortableTextBlockType;
  _createdAt: string;
  date?: string;
  publicationDate?: string;
}

interface SearchProps {
  searchTerm: string;
  shouldFetch: boolean;
}

const Search: React.FC<SearchProps> = ({ searchTerm, shouldFetch }) => {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [filteredResults, setFilteredResults] = useState<SearchResult[]>([]);
  const [noResults, setNoResults] = useState(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 15;

  const types = [
    { value: 'page', label: 'page' },
    { value: 'exhibit', label: 'exhibit' },
    { value: 'event', label: 'event' },
    { value: 'pressRelease', label: 'press release' },
    { value: 'mediaCoverage', label: 'media coverage' },
  ];

  const cookies = new Cookies();
  const preferredLanguage = cookies.get('preferredLanguage') || 'en';

  useEffect(() => {
    const filterResults = () => {
      let filtered = results;
      if (selectedType) {
        filtered = filtered.filter((result) => result._type === selectedType);
      }
      if (preferredLanguage) {
        filtered = filtered.filter((result) => result.language === preferredLanguage);
      }
      setFilteredResults(filtered);
    };
    filterResults();
  }, [selectedType, results, preferredLanguage]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      // Clear results if search term is empty
      setResults([]);
      setFilteredResults([]);
      setNoResults(false);
      return;
    }

    if (!shouldFetch) return; // Don't fetch if shouldFetch is false

    const fetchData = async () => {
      setNoResults(false);

      const query = `*[_type in $selectedTypes && title match $searchTerm] | order(coalesce(publicationDate, date, _createdAt) desc) {
        _id,
        _type,
        title,
        shortTitle,
        slug {
          current
        },
        pageParent->{
          slug {
            current
          },
          pageParent->{
            slug {
              current
            }
          }
        },
        language,
        externalUrl,
        blurb,
        abstract,
        content,
        date,
        publicationDate,
        _createdAt,
      }`;

      const params = {
        searchTerm: `${searchTerm}*`,
        selectedTypes: types.map((type) => type.value),
      };

      try {
        const result = await client.fetch<SearchResult[]>(query, params);

        const processedResults = result.map((node) => ({
          ...node,
          content: Array.isArray(node.content) ? node.content.slice(0, 1) : [],
          blurb: Array.isArray(node.blurb) ? node.blurb.slice(0, 1) : [],
        }));

        // Batch state updates
        const hasNoResults = processedResults.length === 0;
        setResults(processedResults);
        setNoResults(hasNoResults);
      } catch (err) {
        console.error('Search query failed:', err);
        // Reset states on error
        setResults([]);
        setNoResults(true);
      }
    };

    fetchData();
    setCurrentPage(1);
  }, [searchTerm, shouldFetch]);

  const handleTypeSelection = (type: string | null) => {
    setSelectedType(type);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const paginatedResults = filteredResults.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const totalItems = filteredResults.length;
  const shownItems = paginatedResults.length;

  return (
    <div className='bg-sand-500'>
      <div className='mx-auto max-w-screen-xl px-5 py-8 md:gap-x-6 md:p-[50px] lg:gap-x-12 xl:gap-x-24'>
        {/* Filter Tabs */}
        <div className='mb-6 flex flex-wrap-reverse items-center justify-between gap-4 border-b-2 border-zinc-300 pb-6 md:flex-nowrap md:gap-3'>
          <p className='text-base font-light leading-5 text-zinc-700'>filter by</p>
          <div className='flex w-full flex-1 flex-wrap gap-2 lowercase md:w-auto'>
            <Button
              variant={!selectedType ? 'default' : 'outline'}
              size='sm'
              onClick={() => handleTypeSelection(null)}
            >
              all results
            </Button>

            {types.map((type) => (
              <Button
                key={type.value}
                variant={selectedType === type.value ? 'default' : 'outline'}
                size='sm'
                onClick={() => handleTypeSelection(type.value)}
              >
                {type.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Dynamic Total and Shown Items */}
        {filteredResults.length !== 0 && (
          <p className='mb-5 text-base font-medium leading-5'>
            {shownItems} of {totalItems} items shown
          </p>
        )}

        {/* Results */}
        {!noResults && (
          <div className='grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3'>
            {paginatedResults.map((result) => (
              <DetailCard
                key={result._id}
                result={result}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {filteredResults.length > itemsPerPage && (
          <Pagination
            currentPage={currentPage}
            totalItems={filteredResults.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        )}

        {filteredResults.length === 0 && (
          <div className='text-center text-xl font-bold text-gray-500'>no results</div>
        )}
      </div>
    </div>
  );
};

export default Search;
